import { getApiBaseUrl } from '@/app/runtime';
import i18n from '@/i18n';
import { showToast } from '@/plugins/toast';
import { resolveAuthenticatedLandingHash } from '@/utils/authRedirect';
import axios from 'axios';
import normalizeBaseDomain from './normalizeBaseDomain';
import {
  isOnLoginRoute as isOnLoginRouteShared,
  isOnPublicRoute,
  redirectToLoginOnSessionExpired,
  refreshAccessToken,
} from './sharedTokenRefresh';
import { notifyInsufficientTokens } from './tokenErrors';

const debug = false;
const baseDomain = normalizeBaseDomain(getApiBaseUrl());
const baseURL = baseDomain;

function isOnLoginRoute() {
  const hash = window.location.hash || '';
  const pathname = window.location.pathname || '';
  return hash.startsWith('#/login') || pathname === '/login';
}

function redirectToHomeIfOnLogin() {
  if (isOnLoginRoute()) {
    window.location.assign(resolveAuthenticatedLandingHash());
  }
}

const request = axios.create({ baseURL });

request.interceptors.request.use(
  (config) => {
    const accessToken = window.sessionStorage.getItem('accessToken');

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    config.headers['Accept-Language'] = i18n.global.locale.value;

    return config;
  },
  (error) => Promise.reject(error)
);

request.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const status = response?.status ?? 200;

    notifyInsufficientTokens(response, config);

    // Only handle 401 Unauthorized errors. Skip recursive 401s (the request
    // we'd be re-issuing came back with a 401 too — the refreshed token is
    // also bad, so don't keep cycling).
    if (status === 401 && !config._retry) {
      // Already on /login? Don't attempt to refresh — a failed refresh would
      // redirect, which would reload, which would re-fire the very 401
      // we're handling. Plain reject and let the page render normally.
      if (isOnLoginRouteShared()) {
        return Promise.reject(error);
      }
      config._retry = true;
      try {
        // SHARED refresh — see sharedTokenRefresh.js. All clients join the
        // same in-flight refresh promise, preventing the parallel-refresh
        // race that used to invalidate the rotating refresh token and
        // trigger the session_expired redirect loop.
        const accessToken = await refreshAccessToken({ rejectedAccessToken: config.headers.Authorization?.replace(/^Bearer /, '') });
        config.headers.Authorization = `Bearer ${accessToken}`;
        redirectToHomeIfOnLogin();
        return request(config);
      } catch (err) {
        // `refreshAccessToken` already wiped the stale tokens (when it
        // had any). Bounce to /login only when we're not already there.
        if (!isOnPublicRoute()) {
          showToast({
            text: i18n.global.t('messages.session_expired'),
            tone: 'danger',
            durationMs: 4000,
          });
        }
        redirectToLoginOnSessionExpired();
        return Promise.reject(err);
      }
    }

    // For all other errors (500, 404, etc.), just reject without logging out
    return Promise.reject(error);
  }
);

export default request;
